/* ==========================================================================
   GSAP-loader voor de secties van Elisabeth

   Laadt GSAP en zijn plugins op aanvraag, één keer per pagina, en registreert
   de plugins. Meerdere secties op dezelfde pagina delen zo dezelfde
   GSAP-instantie in plaats van elk hun eigen kopie op te halen.

   ---------------------------------------------------------------------------
   Gebruik in een sectie
   ---------------------------------------------------------------------------
   In Liquid zet de sectie de bestandsadressen als data-attributen op zijn
   wortelelement, want alleen Liquid kent de asset-url:

     <div data-mijn-sectie {% render 'el-gsap-data', plugins: 'ScrollTrigger,DrawSVGPlugin' %}>

   In het script van de sectie:

     const { gsap } = await window.elGsap.laad(el, ['ScrollTrigger', 'DrawSVGPlugin']);

   Wie beweging heeft uitgezet, krijgt geen animatie. Vraag dat vooraf:

     if (window.elGsap.verminderdeBeweging()) { toonEindstand(); return; }

   ---------------------------------------------------------------------------
   Wat bewust zo is
   ---------------------------------------------------------------------------
   Dit bestand staat meerdere keren op een pagina als er meerdere secties zijn
   die het laden. Een gewoon script draait bij elke tag opnieuw, dus de eerste
   regel beschermt daartegen.

   Mislukt het laden van een script, dan wordt de belofte afgewezen. Een sectie
   hoort dan zijn eindstand te tonen en niet leeg te blijven: zie de catch in
   het voorbeeld hierboven.
   ========================================================================== */

(() => {
  if (window.elGsap) return;

  // Naam van de global die een script aanmaakt, en het data-attribuut dat zijn adres draagt
  const BRONNEN = {
    gsap: { global: 'gsap', attribuut: 'gsapCore' },
    ScrollTrigger: { global: 'ScrollTrigger', attribuut: 'gsapScrolltrigger' },
    DrawSVGPlugin: { global: 'DrawSVGPlugin', attribuut: 'gsapDrawsvg' },
    SplitText: { global: 'SplitText', attribuut: 'gsapSplittext' },
    Flip: { global: 'Flip', attribuut: 'gsapFlip' },
    MotionPathPlugin: { global: 'MotionPathPlugin', attribuut: 'gsapMotionpath' },
    CustomEase: { global: 'CustomEase', attribuut: 'gsapCustomease' },
    Draggable: { global: 'Draggable', attribuut: 'gsapDraggable' },
    InertiaPlugin: { global: 'InertiaPlugin', attribuut: 'gsapInertia' },
  };

  const cache = new Map();
  const traag = window.matchMedia('(prefers-reduced-motion: reduce)');

  function laadScript(src, globalNaam) {
    if (globalNaam && window[globalNaam]) return Promise.resolve();
    if (!src) return Promise.reject(new Error('script-URL ontbreekt'));
    if (cache.has(src)) return cache.get(src);

    const belofte = new Promise((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = false; // volgorde bewaren
      el.onload = () => resolve();
      el.onerror = () => {
        cache.delete(src); // een volgende poging mag opnieuw proberen
        reject(new Error(`kon ${src} niet laden`));
      };
      document.head.appendChild(el);
    });

    cache.set(src, belofte);
    return belofte;
  }

  /* Laadt GSAP en de gevraagde plugins voor dit element. Geeft
     { gsap, ScrollTrigger, ... } terug met wat er geladen is. */
  async function laad(el, plugins = []) {
    const data = (el && el.dataset) || {};
    const kern = BRONNEN.gsap;

    await laadScript(data[kern.attribuut], kern.global);

    await Promise.all(
      plugins.map((naam) => {
        const b = BRONNEN[naam];
        if (!b) return Promise.reject(new Error(`onbekende plugin ${naam}`));
        return laadScript(data[b.attribuut], b.global);
      })
    );

    const gsap = window.gsap;
    const geladen = { gsap };
    plugins.forEach((naam) => {
      const plugin = window[BRONNEN[naam].global];
      if (plugin) {
        gsap.registerPlugin(plugin);
        geladen[naam] = plugin;
      }
    });
    return geladen;
  }

  window.elGsap = {
    laad,
    verminderdeBeweging: () => traag.matches,
    // Voor een sectie die meebeweegt als de voorkeur tijdens het bezoek wijzigt
    opVoorkeurWijziging: (fn) => {
      traag.addEventListener('change', fn);
      return () => traag.removeEventListener('change', fn);
    },
  };
})();
